import { CheckIcon } from "@heroicons/react/20/solid";
import {
  useMeeting,
  useParticipant,
  usePubSub,
} from "@videosdk.live/react-sdk";
import { useEffect, useMemo, useRef } from "react";
import { toast, Bounce } from "react-toastify";
import useIsMobile from "../utils/useIsMobile";
import useIsTab from "../utils/useIsTab";
import useIsBigDesktop from "../utils/useIsBigScreen";
import { useAppContext } from "../AppContextDef";
import { useTranslation } from "react-i18next";
import useIsLGDesktop from "../utils/useIsLGDesktop";
import { fontMap } from "../utils/common";
import usePubsubFunction from "../utils/usePubsubFunction";
import useFaceVerification from "../hooks/useFaceVerification";
const NETWORK_TOAST_ID = "bad-network-bitrate";
const LOW_BITRATE_KBPS = 30;

const ParticipantView = ({
  currentPage,
  capturedImage,
  setCapturedImage,
  setAgreed,
  agreed,
  abortSignal,
  videoHeight,
}) => {
  let isMobile = useIsMobile();
  let isTab = useIsTab();
  let isBigDesktop = useIsBigDesktop();
  let isLGDesktop = useIsLGDesktop();
  const pubsubPublishMethod = usePubsubFunction();
  const micRef = useRef(null);
  const videoRef = useRef(null);
  let isMobileOrTab = isMobile || isTab;

  // The recorder now owns the verification pipeline and is the sole publisher of
  // the verdict channels. The client only renders from those (via context, fed by
  // ClientPubSubHandler) and runs a single API as a fallback when delegated.
  const {
    selectedLanguageCode,
    isMeetingEnded,
    allDetails,
    faceVerified,
    agreedData,
    faceDetectError,
    isAbortSignal,
    delegatedApi,
    setFaceVerified,
    setAgreedData,
    setIsNetworkUnstable,
  } = useAppContext();

  const { publish: delegatedApiSuccessPublish } = usePubSub(
    `DELEGATED_API_SUCCESS`,
  );

  const { t, i18n } = useTranslation();

  const changeLanguage = (lng) => {
    i18n.changeLanguage(lng);
  };

  useEffect(() => {
    changeLanguage(selectedLanguageCode);
  }, [selectedLanguageCode]);

  const { localParticipant } = useMeeting({});
  const {
    webcamStream,
    micStream,
    webcamOn,
    micOn,
    isLocal,
    captureImage,
    getVideoStats,
  } = useParticipant(localParticipant?.id);

  const fontFamilyCSS = fontMap[selectedLanguageCode] || "Lato";

  // This view remounts per page; start unverified (showing the "verifying"
  // overlay) until the recorder publishes fresh results for the new page.
  useEffect(() => {
    setFaceVerified(false);
    setAgreedData(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Keep the AbortController and the facematch reference frame fresh for the
  // delegated runner. capturedImage persists across page remounts in
  // MeetingContainer, so we mirror it into a ref.
  const abortRef = useRef(abortSignal);
  useEffect(() => {
    abortRef.current = abortSignal;
  }, [abortSignal]);

  const localRefImageRef = useRef(capturedImage);
  useEffect(() => {
    localRefImageRef.current = capturedImage;
  }, [capturedImage]);

  const faceVerification = useFaceVerification({
    mode: "delegate",
    captureImage,
    webcamOn,
    token: allDetails?.videoSDKToken,
    roomId: allDetails?.videoSDKRoomId,
    abortSignalRef: abortRef,
    currentPage,
    referenceImageRef: localRefImageRef,
  });

  // Mirror the recorder's verdict into the page-local `agreed` state that gates
  // the "I Agree" button.
  useEffect(() => {
    setAgreed(!!agreedData);
  }, [agreedData]);

  // Capture the customer's reference/proof frame locally once the recorder
  // confirms verification on PersonalDetails. No API call — this only stores the
  // image (uploaded later as proof, and reused if facematch is delegated).
  useEffect(() => {
    if (
      currentPage === "PersonalDetails" &&
      webcamOn &&
      agreedData &&
      (capturedImage === null || capturedImage === undefined)
    ) {
      (async () => {
        const base64Data = await captureImage(500, 400);
        setCapturedImage("data:image/jpeg;base64," + base64Data);
      })();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, webcamOn, agreedData, capturedImage]);

  // Delegated fallback: when the recorder asks (DELEGATE_API), run ONLY that API
  // locally until it succeeds, then notify the recorder and stop.
  useEffect(() => {
    if (!delegatedApi?.delegate || isMeetingEnded) return;
    // Ignore a delegation that was persisted for a previous page.
    if (delegatedApi.currentPage !== currentPage) return;
    const apiName = delegatedApi.apiName;
    let cancelled = false;

    (async () => {
      while (!cancelled) {
        if (abortRef.current?.signal?.aborted) break;
        const ok = await faceVerification.runDelegatedApi(apiName);
        if (cancelled) break;
        if (ok) {
          pubsubPublishMethod(
            delegatedApiSuccessPublish,
            "DelegatedApiSuccess",
            { persist: true },
            { apiName, succeeded: true, currentPage },
          );
          break;
        }
        await new Promise((resolve) => setTimeout(resolve, 2000));
      }
    })();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [delegatedApi, isMeetingEnded]);

  // Show verification error toasts to the customer (driven by recorder publishes).
  useEffect(() => {
    if (faceDetectError == null) return;
    const message =
      faceDetectError?.err === "NO_FACE_DETECTED"
        ? "No face detected. Please ensure your face is clearly visible."
        : faceDetectError?.err === "FACE_MATCH_UNSUCESSFUL"
          ? "Face Match Unsuccessful!"
          : faceDetectError?.err === "MULTIPLE_FACES_DETECTED"
            ? "Multiple faces detected. Please make sure only one person is in view!"
            : faceDetectError?.err === "SPOOF_DETECTED"
              ? "Make sure a real person is in view and looking towards camera in a well-lit area!"
              : faceDetectError?.err === "NO_CAMERA_PERMISSIONS"
                ? "You don't have camera permissions, Please give permissions manually"
                : "";

    if (message && !isAbortSignal) {
      handleToastMessage({ message: t(message) });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [faceDetectError, isAbortSignal]);

  // Once the face is verified, clear any lingering error toasts.
  useEffect(() => {
    if (faceVerified?.isVerified) {
      toast.dismiss();
    }
  }, [faceVerified]);

  const zeroBitrateCountRef = useRef(0);
  const networkToastShownRef = useRef(false);
  const webcamWasOnRef = useRef(false);
  const webcamDroppedRef = useRef(false);

  const showNetworkUnstableToast = () => {
    new Audio(`https://cdn.videosdk.live/icici/error_1.wav`).play();
    toast.error(
      t("Your network seems unstable. Please move to a better network."),
      {
        toastId: NETWORK_TOAST_ID,
        position: "top-right",
        autoClose: false,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
        theme: "light",
        transition: Bounce,
        style: { fontFamily: fontFamilyCSS },
      },
    );
  };

  useEffect(() => {
    if (!webcamOn || !getVideoStats) return;

    const interval = setInterval(async () => {
      try {
        const stats = await getVideoStats();
        const bitrate = stats && stats.length ? stats[0]?.bitrate ?? 0 : 0;
        console.log(
          `[Client] video bitrate: ${bitrate} (low if < ${LOW_BITRATE_KBPS})`,
        );

        if (bitrate < LOW_BITRATE_KBPS) {
          if (zeroBitrateCountRef.current >= 5) {
            zeroBitrateCountRef.current = 0;
          }
          zeroBitrateCountRef.current += 1;
          console.log(
            `[Client] low-bitrate streak: ${zeroBitrateCountRef.current}/5`,
          );
          if (
            zeroBitrateCountRef.current >= 5 &&
            !networkToastShownRef.current
          ) {
            networkToastShownRef.current = true;
            setIsNetworkUnstable(true);
            console.warn(
              "[Client] video bitrate low for 5s — prompting customer to switch network",
            );
            showNetworkUnstableToast();
          }
        } else if (
          zeroBitrateCountRef.current > 0 ||
          networkToastShownRef.current
        ) {
          zeroBitrateCountRef.current = 0;
          if (networkToastShownRef.current) {
            networkToastShownRef.current = false;
            setIsNetworkUnstable(false);
            console.log(
              "[Client] video bitrate recovered — dismissing network toast",
            );
            toast.dismiss(NETWORK_TOAST_ID);
          }
        }
      } catch (err) {
        console.error("[Client] failed to read video stats", err);
      }
    }, 1000);

    return () => {
      clearInterval(interval);
      zeroBitrateCountRef.current = 0;
      networkToastShownRef.current = false;
      setIsNetworkUnstable(false);
      toast.dismiss(NETWORK_TOAST_ID);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, webcamOn]);

  useEffect(() => {
    if (webcamOn) {
      if (webcamDroppedRef.current) {
        webcamDroppedRef.current = false;
        console.log("[Client] webcam track recovered");
        setIsNetworkUnstable(false);
        networkToastShownRef.current = false;
        toast.dismiss(NETWORK_TOAST_ID);
      }
      webcamWasOnRef.current = true;
    } else if (webcamWasOnRef.current) {
      // Track fully torn down — no camera feed at all (the bitrate watchdog is
      // stopped while the webcam is off, so it can't surface the toast). Show
      // the "Reconnecting" placeholder (render), the "network unstable" toast,
      // and disable Start until the webcam comes back.
      console.warn(
        "[Client] webcam track dropped — showing reconnecting + network toast",
      );
      webcamDroppedRef.current = true;
      setIsNetworkUnstable(true);
      networkToastShownRef.current = true;
      showNetworkUnstableToast();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [webcamOn]);

  const handleToastMessage = ({ message }) => {
    new Audio(`https://cdn.videosdk.live/icici/error_1.wav`).play();
    toast.error(message, {
      position: "top-right",
      autoClose: 2000,
      hideProgressBar: false,
      closeOnClick: true,
      pauseOnHover: true,
      draggable: true,
      progress: undefined,
      theme: "light",
      transition: Bounce,
      style: { fontFamily: fontFamilyCSS },
    });
  };

  const videoStream = useMemo(() => {
    if (webcamOn && webcamStream) {
      const mediaStream = new MediaStream();
      mediaStream.addTrack(webcamStream.track);
      return mediaStream;
    }
    return null;
  }, [webcamStream, webcamOn]);

  useEffect(() => {
    if (micRef.current) {
      if (micOn && micStream) {
        try {
          const mediaStream = new MediaStream();
          mediaStream.addTrack(micStream.track);
          micRef.current.srcObject = mediaStream;
          micRef.current
            .play()
            .catch((error) =>
              console.error("micRef.current.play() failed", error),
            );
        } catch (error) {
          console.error("Error handling mic stream:", error);
          micRef.current.srcObject = null;
        }
      } else {
        micRef.current.srcObject = null;
      }
    }
  }, [micStream, micOn]);

  useEffect(() => {
    if (videoRef.current) {
      if (webcamOn && videoStream) {
        try {
          videoRef.current.srcObject = videoStream;
          videoRef.current.setAttribute("playsinline", "");
          videoRef.current.setAttribute("x5-playsinline", "");
          videoRef.current.setAttribute("webkit-playsinline", "");
          videoRef.current
            .play()
            .catch((error) =>
              console.error("videoRef.current.play() failed", error),
            );
        } catch (error) {
          console.error("Error handling webcam stream:", error);
          videoRef.current.srcObject = null;
        }
      } else {
        videoRef.current.srcObject = null;
      }
    }
  }, [videoStream, webcamOn]);

  const isVerified = faceVerified?.isVerified;

  const videoBoxHeight =
    !isMobileOrTab && currentPage !== "VideoConsent"
      ? "490px"
      : isTab
        ? "290px"
        : isBigDesktop && currentPage === "VideoConsent"
          ? videoHeight
          : isBigDesktop
            ? "600px"
            : isMobile
              ? "180px"
              : videoHeight
                ? videoHeight
                : "380px";
  const videoBoxWidth = isMobile
    ? "100%"
    : isTab
      ? "70%"
      : currentPage === "VideoConsent"
        ? isBigDesktop
          ? "50%"
          : isLGDesktop
            ? "100%"
            : "70%"
        : "100%";

  if (!webcamOn) {
    return webcamWasOnRef.current ? (
      <div className="flex lg:flex-1 w-full flex-col items-center justify-center relative md:justify-center">
        <div
          style={{ height: videoBoxHeight, width: videoBoxWidth }}
          className="flex flex-col items-center justify-center gap-4 rounded-xl bg-[#161616]"
        >
          <div
            style={{ borderTopColor: "#EA7617" }}
            className="animate-spin ease-linear rounded-full border-4 border-gray-100 h-12 w-12"
          ></div>
          <p
            style={{ fontFamily: fontFamilyCSS }}
            className="text-white text-base lg:text-lg text-center px-4 mb-0"
          >
            {`${t("Reconnecting")}...`}
          </p>
        </div>
      </div>
    ) : null;
  }

  return (
    <div
      className={`flex lg:flex-1 w-full flex-col items-end justify-end md:items-center relative md:justify-center`}
    >
      {!isVerified && (
        <>
          <div
            style={{
              height:
                !isMobileOrTab && currentPage !== "VideoConsent"
                  ? "490px"
                  : isTab
                    ? "290px"
                    : isBigDesktop && currentPage === "VideoConsent"
                      ? videoHeight
                      : isBigDesktop
                        ? "600px"
                        : isMobile
                          ? "180px"
                          : videoHeight
                            ? videoHeight
                            : "380px",
              width: isMobile
                ? "100%"
                : isTab
                  ? "70%"
                  : currentPage === "VideoConsent"
                    ? isBigDesktop
                      ? "50%"
                      : isLGDesktop
                        ? "100%"
                        : "70%"
                    : "100%",
            }}
            className={`absolute rounded-xl bg-[#16161680] z-30 `}
          >
            {" "}
          </div>

          <div className="absolute z-40 flex flex-col items-center justify-center h-44 p-0">
            <p className="text-white text-xl text-center mt-0 z-50">
              {`${t("Please hold on while we verify your video")}...`}
            </p>
          </div>
        </>
      )}

      <video
        ref={videoRef}
        autoPlay
        playsInline
        muted={true}
        width={
          isMobile
            ? "100%"
            : isTab
              ? "70%"
              : currentPage === "VideoConsent"
                ? isBigDesktop
                  ? "50%"
                  : isLGDesktop
                    ? "100%"
                    : "70%"
                : "100%"
        }
        className={
          isVerified
            ? agreedData
              ? "border-2 border-green-600"
              : "border-2 border-red-700"
            : `relative ${currentPage !== "VideoConsent" && "w-hull h-full"} `
        }
        // className="border border-green-150"
        style={{
          objectFit: "contain",
          borderRadius: 16,
          height:
            !isMobileOrTab && currentPage !== "VideoConsent"
              ? "490px"
              : isTab
                ? "290px"
                : isBigDesktop && currentPage === "VideoConsent"
                  ? videoHeight
                  : isBigDesktop
                    ? "600px"
                    : isMobile
                      ? "180px"
                      : videoHeight
                        ? videoHeight
                        : "380px",
          zIndex: 10,
        }}
      />
      <audio ref={micRef} autoPlay playsInline muted={isLocal} />

      {isVerified &&
        agreedData &&
        currentPage === "VideoConsent" &&
        !isMobileOrTab && (
          <div className="absolute top-0 right-2 xl:right-[150px] 2xl:right-[265px] z-10 bg-white p-1.5 lg:p-2 mt-2 lg:mt-3 flex items-center rounded-md max-w-max">
            <CheckIcon className="w-4 h-4 lg:w-5 lg:h-5 text-green-250" />
            <p className="ml-2 text-green-250 text-sm lg:text-base mb-0">
              {t("Face detected")}
            </p>
          </div>
        )}

      {isVerified && agreedData && isMobileOrTab && (
        <div
          className={`absolute ${
            isMobile ? "bottom-0.5 right-0 left-0.5" : "top-0 md:right-32"
          }  z-10 bg-white p-1.5 lg:p-2 mt-2 lg:mt-3 flex items-center rounded-b-2xl md:rounded-md w-[98%] md:max-w-max`}
        >
          <CheckIcon className="w-4 h-4 lg:w-5 lg:h-5 text-green-250" />
          <p className="ml-2 text-green-250 text-sm lg:text-base mb-0">
            {t("Face detected")}
          </p>
        </div>
      )}

      {isVerified &&
        agreedData &&
        currentPage !== "VideoConsent" &&
        !isMobileOrTab && (
          <div className="bg-[#3BA55D1A] p-1.5 lg:p-2 mt-2 lg:mt-3 flex items-center rounded-md max-w-max">
            <CheckIcon className="w-4 h-4 lg:w-5 lg:h-5 text-green-250" />
            <p className="ml-2 text-green-250 text-sm lg:text-base mb-0">
              {t("Face detected")}
            </p>
          </div>
        )}
    </div>
  );
};

export default ParticipantView;
